import { Component, HostListener } from "@angular/core";
import {
  ColDef,
  ExcelStyle,
  GridApi,
  GridOptions,
  GridReadyEvent,
  SelectionChangedEvent,
} from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { SharedDataService } from "../../services/sharedDataService";
import { LoadingScreenService } from "../../shared/loading-screen.service";
import { GridViewBaseComponent } from "../../utility/grid-view-base.component";
import { ApiService } from "../../services";
import { PfRequest } from "../../generated-models/PfRequest";
import { PfSubRequest } from "../../generated-models/PfSubRequest";
import { ModalService } from "../../shared/modal.service";
import { PfRequestViewCellComponent } from "./pf-request-view-cell.component";
import { configurePfSubRequestExpansion } from "./pf-subrequest-grid";
import { exportExcel, getExportedExcelFileNameSuffix } from "../../utility/common-helper";

interface PfRequestGridRow {
  id: number;
  requestId: string;
  serviceTreeId: string;
  serviceTeam: string;
  region: string;
  purpose: string;
  manatree: string;
  status: string;
  createdTime: Date;
  createdBy: string;
  subRequests: PfSubRequest[];
}

@Component({
  standalone: false,
  templateUrl: "./pf-ad-hoc-requests.component.html",
  styleUrls: ["../../styles.scss", "./pf.scss"],
  styles: [
    `
      .request-filter-toggle {
        margin-right: 0.75rem;
        white-space: nowrap;
      }

      .request-filter-toggle .form-check-label {
        white-space: nowrap;
      }
    `,
  ],
})
export class PfAdHocRequestsComponent extends GridViewBaseComponent {
  gridOptions: GridOptions;
  gridApi: GridApi;
  initPageSize = 15;
  rowData: PfRequestGridRow[] = [];
  allRequestRows: PfRequestGridRow[] = [];
  isAdmin = false;
  showMyServiceTeamRequestsOnly = false;
  showApprovedRequestsOnly = false;
  hasSelectedRequests = false;
  myServiceTreeIds: string[] = [];
  headerStyle: ExcelStyle[] = [
    {
      id: "header",
      font: {
        bold: true,
      },
    },
  ];

  keyFilterStats = "filter:ad-hoc-requests";

  constructor(
    protected apiService: ApiService,
    protected sharedDataService: SharedDataService,
    private loadingService: LoadingScreenService,
    private notificationService: ToastrService,
    private modalService: ModalService
  ) {
    super(apiService, sharedDataService);
  }

  ngOnInit() {
    const columnDefs = this.buildColumnDefs();

    super.InitAgGrid(columnDefs);

    this.gridOptions.rowSelection = "multiple";
    this.gridOptions.rowMultiSelectWithClick = true;
    this.gridOptions.suppressRowClickSelection = true;
    configurePfSubRequestExpansion(this.gridOptions);
    this.refreshData();
  }

  override onProfileLoaded() {
    this.isAdmin = this.userProfile?.IsAdmin ?? false;
    this.showMyServiceTeamRequestsOnly = false;
    this.hasSelectedRequests = false;

    if (this.gridOptions) {
      const columnDefs = this.buildColumnDefs();
      this.gridOptions.columnDefs = columnDefs;
      this.gridOptions.suppressRowClickSelection = !this.isAdmin;
      this.gridApi?.setGridOption("columnDefs", columnDefs);
      this.gridApi?.setGridOption("suppressRowClickSelection", !this.isAdmin);
    }
  }

  private buildColumnDefs(): ColDef[] {
    return [
      {
        headerName: "Request ID",
        field: "requestId",
        filter: true,
        sortable: true,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          innerRenderer: PfRequestViewCellComponent,
        },
      },
      {
        headerName: "Service Tree ID",
        field: "serviceTreeId",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Service Team",
        field: "serviceTeam",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Region",
        field: "region",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Purpose",
        field: "purpose",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Manatree",
        field: "manatree",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Status",
        field: "status",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Created By",
        field: "createdBy",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Created Date",
        field: "createdTime",
        filter: true,
        sortable: true,
      },
    ];
  }

  onGridReady(params: GridReadyEvent) {
    super.onGridReady(params);
    this.gridApi.setFilterModel({
      customFilter: { values: ["true"] },
    });
  }

  async refreshData() {
    this.loadingService.setLoading(true);

    try {
      this.allRequestRows = await this.getPfRequests();
      this.applyTableFilters();
      super.setupFilterByLocalData();
    } catch (_error) {
      this.allRequestRows = [];
      this.rowData = [];
      this.notificationService.error("Failed to load PF requests");
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  private async getPfRequests(): Promise<PfRequestGridRow[]> {
    const [requests, teams] = await Promise.all([
      this.apiService.getAllPfRequests().toPromise(),
      this.apiService.getTeamList().toPromise(),
    ]);
    const serviceTeamNames = new Map(teams.map((team) => [team.Oid, team.Name]));

    return requests.map((pfRequest: PfRequest) => ({
      id: pfRequest.Id,
      requestId: pfRequest.Id.toString(),
      serviceTreeId: pfRequest.ServiceTreeId,
      serviceTeam: serviceTeamNames.get(pfRequest.ServiceTreeId) ?? "",
      region: pfRequest.Region,
      purpose: pfRequest.Purpose,
      manatree: pfRequest.Manatree,
      status: pfRequest.Status,
      createdTime: pfRequest.CreatedTime,
      createdBy: pfRequest.CreatedBy,
      subRequests: pfRequest.SubRequests ?? [],
    }));
  }

  private applyTableFilters() {
    let requestRows = this.allRequestRows;

    if (this.showApprovedRequestsOnly) {
      requestRows = requestRows.filter((request) => this.isApprovedRequest(request.status));
    }

    if (this.showMyServiceTeamRequestsOnly) {
      requestRows = requestRows.filter((request) => this.myServiceTreeIds.includes(request.serviceTreeId));
    }

    this.rowData = requestRows;
    if (this.gridApi) {
      this.gridApi.setGridOption("rowData", this.rowData);
      this.gridApi.deselectAll();
    }
    this.hasSelectedRequests = false;
  }

  private async getMyServiceTreeIdsIfNeeded(): Promise<string[]> {
    if (!this.showMyServiceTeamRequestsOnly) {
      return this.myServiceTreeIds;
    }

    if (!this.myServiceTreeIds.length) {
      this.myServiceTreeIds = (await this.apiService.getMyPfPlans().toPromise()).map((plan) => plan.ServiceTreeId);
    }

    return this.myServiceTreeIds;
  }

  onSelectionChanged(event: SelectionChangedEvent) {
    if (!this.isAdmin) {
      this.hasSelectedRequests = false;
      return;
    }

    this.hasSelectedRequests = event.api.getSelectedRows().length > 0;
  }

  async onShowMyServiceTeamRequestsOnlyChanged() {
    if (this.showMyServiceTeamRequestsOnly) {
      this.loadingService.setLoading(true);
      try {
        await this.getMyServiceTreeIdsIfNeeded();
      } catch (_error) {
        this.showMyServiceTeamRequestsOnly = false;
        this.notificationService.error("Failed to load your service teams");
      } finally {
        this.loadingService.setLoading(false);
      }
    }

    this.applyTableFilters();
  }

  onShowApprovedRequestsOnlyChanged() {
    this.applyTableFilters();
  }


  async deleteSelectedRequests() {
    if (!this.isAdmin) {
      return;
    }

    const selectedRows = (this.gridApi?.getSelectedRows() ?? []) as PfRequestGridRow[];
    if (selectedRows.length === 0) {
      this.notificationService.warning("Please select at least one PF request to delete.");
      return;
    }

    try {
      const requestLabel = selectedRows.length === 1 ? "request" : "requests";
      await this.modalService.confirmationModal(
        `You are about to delete <strong>${selectedRows.length} selected PF ${requestLabel}</strong>.<br>This action cannot be undone. Do you want to continue?`
      );
    } catch {
      return;
    }

    this.loadingService.setLoading(true);

    try {
      await Promise.all(selectedRows.map((request) => this.apiService.deletePfRequest(request.id).toPromise()));
      this.notificationService.success(`${selectedRows.length} PF request(s) deleted successfully.`);
      await this.refreshData();
    } catch (_error) {
      this.notificationService.error("Failed to delete the selected PF requests.");
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  private isApprovedRequest(status: string): boolean {
    return status === "SignedOff" || status === "Approved";
  }

  @HostListener("window:resize")
  onResize() {
    setTimeout(() => {
      this.gridApi?.autoSizeAllColumns();
    }, 100);
  }

  exportExcel() {
    const fileName = "PfAdHocRequests-" + getExportedExcelFileNameSuffix();
    const sheetName = "PF Ad Hoc Requests";

    exportExcel(this.gridApi, fileName, sheetName);
  }
}
